/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useDispatch, useSelector } from "react-redux"
import { useEffect, useRef } from "react"
import { actionCreator } from "store/duck/global"
import { AppState } from "store/store.type"
import { DELV_PLATFORM_FEATURE_KEYS } from "types/delv-platform-api"
import { useParams } from "react-router-dom"
import useInventoryContext from "./useInventoryContext"

const useDelvPlatformFeatureFlags = (
  featureKeys: DELV_PLATFORM_FEATURE_KEYS[],
) => {
  const dispatch = useDispatch()
  const { delvPlatformFeatureFlags } = useSelector(
    (state: AppState) => state.global,
  )
  const { menuParentID } = useParams<{
    menuParentID: string
    tabKey: string
  }>()
  const featureKeyRef = useRef(featureKeys)

  const { isMenuGroup, merchantGroupID } = useSelector(
    (state: AppState) => state.menu,
  )

  const { isInventory } = useInventoryContext()

  // suppose to be run only once
  useEffect(() => {
    if (menuParentID && featureKeyRef.current?.length > 0) {
      let menuGroupID: string | undefined
      let merchantID: string | undefined

      if (isInventory) {
        // in Inventory page, 'menuParentID' treated as 'merchantID'.
        merchantID = menuParentID
      } else {
        // for  Menu page, 'menuParentID' can be either a 'merchantID' or 'MenuGroupId'.
        // if 'isMenuGroup' is true, 'menuParentID' is treated as a 'menuGroupID'.
        // if 'isMenuGroup' is false, indicating a single store, 'menuParentID' is treated as a 'merchantID'.
        menuGroupID = isMenuGroup ? menuParentID : undefined
        merchantID = isMenuGroup ? undefined : menuParentID
      }

      dispatch(
        actionCreator.fetchDelvPlatformFeatureFlags(
          {
            featureKeys: featureKeyRef.current,
            merchantID,
            menuGroupID,
          },
          merchantID || "",
          merchantGroupID,
        ),
      )
    }
  }, [dispatch, menuParentID, isMenuGroup, isInventory, merchantGroupID])

  return delvPlatformFeatureFlags
}

export default useDelvPlatformFeatureFlags
